/**
 * analytics.ts — PostHog product-analytics wrapper (T-126)
 *
 * One question v1 answers: "is the pilot actually using this, and which features?"
 *
 * Design contract (see docs/EVENTS.md for the full dictionary):
 *  - KILL-SWITCH: everything no-ops when VITE_POSTHOG_KEY is absent. With no key we
 *    NEVER call posthog.init(), so there are ZERO network calls — this lets the code
 *    ship and be tested before the account exists, and is the off-switch later.
 *  - Bundled SDK: posthog-js is imported from our own origin (npm), so the CSP
 *    script-src stays 'self'; only connect-src gains https://us.i.posthog.com.
 *  - Privacy: autocapture + session replay OFF; identified-only person profiles.
 *    Events carry COARSE geo only (county/state FIPS, viewport centers rounded to 2dp).
 *    Full addresses go ONLY to our own Postgres (search_events), never to the vendor.
 *  - Deliberate taxonomy: snake_case object_action names; dynamic values live in
 *    properties, never in event names. The EventProps map below is the schema — the
 *    typed track() helper makes an off-schema property a compile error.
 */
import posthog from 'posthog-js'
import type { MapTier } from './mapTiers'
import type { AuthUser } from '../auth/AuthContext'

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const POSTHOG_HOST = 'https://us.i.posthog.com'

let initialized = false

/**
 * Initialise PostHog once, at app boot. No-op (and zero network) when the key is
 * absent — the kill-switch. Safe to call more than once.
 */
export function initAnalytics(): void {
  if (initialized) return
  if (!POSTHOG_KEY) return // kill-switch: no key → never init → no network calls

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: false, // deliberate taxonomy only — no DOM autocapture
    capture_pageview: false, // SPA; we track meaningful events, not page loads
    capture_pageleave: false,
    disable_session_recording: true, // pilot privacy — no replay
    person_profiles: 'identified_only', // no anonymous profiles pre-login
    // T-540: never fetch remote config as a SCRIPT. posthog-js would load
    // us-assets.i.posthog.com/array/<token>/config.js and only fall back to the JSON
    // /array/<token>/config when that fails — but our CSP allows the JSON path alone, because
    // a remote script origin would reverse the T-126 decision pinned in apps/api/tests/csp.test.ts
    // (tokens live in localStorage; XSS→token theft is mitigated by "no third-party scripts").
    // This aligns the client with the policy instead of loosening the policy to fit the client:
    // the SDK skips the script attempt, so the browser never has a request to refuse.
    disable_external_dependency_loading: true,
  })
  initialized = true
}

/** Round a coordinate to 2 decimals (~1 km) — coarse geo for vendor payloads. */
export function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/**
 * Derive COARSE geo (county + state) from an 11-digit tract GEOID
 * (2-digit state FIPS + 3-digit county + 6-digit tract). Returns the 5-digit county
 * GEOID and a state label (abbreviation when a FIPS→abbrev resolver is supplied,
 * else the raw 2-digit state FIPS). No street-level precision ever leaves here.
 */
export function coarseGeoFromGeoid(
  geoid: string | null | undefined,
  fipsToAbbrev?: (stateFips: string) => string | undefined,
): { county: string | null; state: string | null } {
  if (!geoid || geoid.length < 5) return { county: null, state: null }
  const stateFips = geoid.slice(0, 2)
  const countyFips = geoid.slice(0, 5) // standard 5-digit county GEOID
  return { county: countyFips, state: fipsToAbbrev?.(stateFips) ?? stateFips }
}

/**
 * The event dictionary AS TYPES. Each key is an event name; its value is the exact
 * property schema. track() is generic over this map, so a wrong/missing property is a
 * TypeScript error — the schema can't drift from the code silently.
 */
export interface EventProps {
  user_logged_in: { method?: string }
  address_searched: { county: string | null; state: string | null; result_found: boolean }
  pin_dropped: {
    method: 'click' | 'drag'
    tract_geoid: string | null
    county: string | null
    state: string | null
    center_lat: number
    center_lng: number
  }
  map_viewport_changed: {
    zoom: number
    zoom_tier: MapTier
    center_lat: number
    center_lng: number
    trigger: 'load' | 'gesture'
  }
  map_zoom_tier_changed: { from_tier: MapTier; to_tier: MapTier }
  layer_toggled: { layer_id: string; enabled: boolean }
  tract_selected: { geoid: string; county: string | null; state: string | null }
  metrics_panel_opened: { context: string; source: string }
  filter_applied: { filter_name: string; value_count: number }
  // T-500: a shareable deep-link was copied — the top of the acquisition funnel. Coarse only:
  // whether the link carried a tract/pin selection, never the location itself.
  share_link_created: { has_selection: boolean }
}

/**
 * Fire a product-analytics event. Type-safe: `properties` must match the schema for
 * `event` in EventProps. No-op for the network when uninitialised (kill-switch), but
 * always emits a dev-only console breadcrumb so the full taxonomy is verifiable in the
 * browser without a live PostHog project (a console log is NOT a network call, so the
 * kill-switch guarantee is intact).
 */
export function track<K extends keyof EventProps>(event: K, properties: EventProps[K]): void {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[analytics]', event, properties)
  }
  if (!initialized) return
  posthog.capture(event, properties as Record<string, unknown>)
}

/**
 * Identify the signed-in user + set the org group. Called on every authenticated
 * session (fresh login AND restored session) so "is this account engaged" is
 * first-class. Sends only the email DOMAIN and plan as person props — never the full
 * email. There is no org claim in the Cognito token; the pilot is one company, so the
 * email domain is the org group key.
 */
export function identifyUser(user: AuthUser): void {
  if (!initialized) return
  const emailDomain = user.email && user.email.includes('@') ? user.email.split('@')[1] : undefined

  posthog.identify(user.sub, {
    email_domain: emailDomain,
    plan: user.plan,
  })

  if (emailDomain) {
    posthog.group('org', emailDomain, { domain: emailDomain })
  }
}

/** Clear identity on sign-out so the next user isn't merged into this one's profile. */
export function resetAnalytics(): void {
  if (!initialized) return
  posthog.reset()
}
